import React from 'react';
import { cn } from '../../utils/cn';
import { useI18n } from '../../i18n/I18nContext';

export type TimeSlot = { openTime: string; closeTime: string };
export type DayHours = { isOpen: boolean; time: TimeSlot[] };
export type WeekHours = Record<number, DayHours>;

const emptyDay = (): DayHours => ({ isOpen: false, time: [] });

type ScheduleType = { key: string; label: string };

type Props = {
  types: ScheduleType[];
  hours: Record<string, WeekHours>;
  onChange: (typeKey: string, week: WeekHours) => void;
};

function safeTimes(data: DayHours): TimeSlot[] {
  return Array.isArray(data?.time) ? data.time : [];
}

function DayRow({
  day,
  data,
  onToggle,
  onAddSlot,
  onRemoveSlot,
  onChangeSlot,
}: {
  day: number;
  data: DayHours;
  onToggle: (open: boolean) => void;
  onAddSlot: () => void;
  onRemoveSlot: (i: number) => void;
  onChangeSlot: (i: number, field: 'openTime' | 'closeTime', v: string) => void;
}) {
  const { t } = useI18n();
  const times = safeTimes(data);
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 cursor-pointer">
          <div
            className={cn(
              'relative h-5 w-9 flex-shrink-0 rounded-full transition-colors duration-200',
              data.isOpen ? 'bg-red-600' : 'bg-slate-300 dark:bg-slate-600'
            )}
          >
            <span
              className={cn(
                'absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform duration-200',
                data.isOpen ? 'translate-x-4' : 'translate-x-0'
              )}
            />
            <input
              type="checkbox"
              className="sr-only"
              checked={data.isOpen}
              onChange={(e) => onToggle(e.target.checked)}
            />
          </div>
          <span className="text-sm font-medium text-slate-800 hidden md:inline dark:text-slate-200">{t(`common.days.${day}`)}</span>
          <span className="text-sm font-medium text-slate-800 md:hidden dark:text-slate-200">{t(`common.daysShort.${day}`)}</span>
        </label>
        {data.isOpen && (
          <button
            type="button"
            onClick={onAddSlot}
            className="text-xs font-medium text-red-600 hover:text-red-700 dark:text-red-400"
          >
            {t('common.operatingHours.addSlot')}
          </button>
        )}
      </div>

      {data.isOpen && times.length === 0 && (
        <p className="pl-0 text-xs text-slate-400 sm:pl-11">{t('common.operatingHours.noSlots')}</p>
      )}

      {data.isOpen &&
        times.map((slot, i) => (
          <div key={i} className="flex items-center gap-2 pl-0 sm:pl-11">
            <input
              type="time"
              value={slot.openTime}
              onChange={(e) => onChangeSlot(i, 'openTime', e.target.value)}
              className="min-w-0 flex-1 rounded-md border sm:w-[120px] sm:flex-none border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-900 outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            />
            <span className="text-xs text-slate-400">{t('common.operatingHours.to')}</span>
            <input
              type="time"
              value={slot.closeTime}
              onChange={(e) => onChangeSlot(i, 'closeTime', e.target.value)}
              className="min-w-0 flex-1 rounded-md border sm:w-[120px] sm:flex-none border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-900 outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            />
            <button
              type="button"
              onClick={() => onRemoveSlot(i)}
              className="ml-1 rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-500 transition dark:hover:bg-rose-950"
              aria-label={t('common.operatingHours.removeSlot')}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}

      {!data.isOpen && (
        <p className="pl-0 text-xs text-slate-400 sm:pl-11">{t('common.operatingHours.closed')}</p>
      )}
    </div>
  );
}

export function OperatingHours({ types, hours, onChange }: Props) {
  const update = (typeKey: string, day: number, partial: Partial<DayHours>) => {
    const week = { ...hours[typeKey] };
    week[day] = { ...(week[day] ?? emptyDay()), ...partial };
    onChange(typeKey, week);
  };

  const toggleDay = (typeKey: string, day: number, open: boolean) => {
    if (!open) {
      update(typeKey, day, { isOpen: false, time: [] });
    } else {
      update(typeKey, day, { isOpen: true });
    }
  };

  const addSlot = (typeKey: string, day: number) => {
    const current = hours[typeKey]?.[day] ?? emptyDay();
    const times = safeTimes(current);
    update(typeKey, day, {
      isOpen: true,
      time: [...times, { openTime: '', closeTime: '' }],
    });
  };

  const removeSlot = (typeKey: string, day: number, idx: number) => {
    const current = hours[typeKey]?.[day] ?? emptyDay();
    const newTime = safeTimes(current).filter((_, i) => i !== idx);
    update(typeKey, day, {
      isOpen: newTime.length > 0,
      time: newTime,
    });
  };

  const changeSlot = (
    typeKey: string,
    day: number,
    idx: number,
    field: 'openTime' | 'closeTime',
    value: string
  ) => {
    const current = hours[typeKey]?.[day] ?? emptyDay();
    const newTime = safeTimes(current).map((s, i) => (i === idx ? { ...s, [field]: value } : s));
    update(typeKey, day, { time: newTime });
  };

  const cols = types.length === 1 ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2';

  return (
    <div className={`grid gap-6 ${cols}`}>
      {types.map(({ key, label }) => (
        <div key={key}>
          <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-300">{label}</h3>
          <div className="space-y-2">
            {[0, 1, 2, 3, 4, 5, 6].map((day) => (
              <DayRow
                key={day}
                day={day}
                data={hours[key]?.[day] ?? emptyDay()}
                onToggle={(open) => toggleDay(key, day, open)}
                onAddSlot={() => addSlot(key, day)}
                onRemoveSlot={(i) => removeSlot(key, day, i)}
                onChangeSlot={(i, field, v) => changeSlot(key, day, i, field, v)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
